import { useState, useEffect } from 'react';
import { useAuthStore, api } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import { useDepos } from '../lib/useDepos';
import { cn } from '../lib/utils';
import { turkishIncludes } from '../lib/turkishSearch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { format, startOfMonth, endOfMonth, startOfYear, endOfYear, startOfWeek, endOfWeek, subDays } from 'date-fns';
import { tr, enUS, nl, fr } from 'date-fns/locale';
import * as XLSX from 'xlsx';
import {
  Loader2,
  Calendar as CalendarIcon,
  BarChart3,
  PieChart as PieChartIcon,
  Briefcase,
  Heart,
  Coffee,
  Palmtree,
  HelpCircle,
  Users,
  TrendingUp,
  Filter,
  Download,
  RefreshCw,
  Search,
  AlertTriangle,
  Trash2
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';

const getDateLocale = (lang) => {
  switch (lang) {
    case 'tr': return tr;
    case 'nl': return nl;
    case 'fr': return fr;
    default: return enUS;
  }
};

const STATUS_ICONS = {
  WORKING: Briefcase,
  HALF_DAY: Coffee,
  SICK: Heart,
  DOP: Coffee,
  ON_LEAVE: Palmtree,
  NONE: HelpCircle
};

const MONTH_NAMES = {
  tr: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  nl: ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'],
  fr: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']
};

export const AdminWorkStatistics = () => {
  const { t, language } = useTranslation();
  const { depos: dbDepos } = useDepos();
  const { user } = useAuthStore();
  const dateLocale = getDateLocale(language);
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const isAnyAdmin = user?.role === 'SUPER_ADMIN' || user?.role === 'FULL_ADMIN' || user?.role === 'FIRM_ADMIN';
  const canFilterFirm = user?.role === 'SUPER_ADMIN' || user?.role === 'FULL_ADMIN';
  
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [labels, setLabels] = useState({});
  const [colors, setColors] = useState({});
  const [firms, setFirms] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [orphanedRecords, setOrphanedRecords] = useState(null);
  const [showOrphanedDialog, setShowOrphanedDialog] = useState(false);
  const [deletingOrphaned, setDeletingOrphaned] = useState(false);
  
  // Filters
  const [selectedFirm, setSelectedFirm] = useState('all');
  const [selectedDepo, setSelectedDepo] = useState('all');
  const [dateRange, setDateRange] = useState({
    start: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    end: format(endOfMonth(new Date()), 'yyyy-MM-dd')
  });
  const [quickRange, setQuickRange] = useState('month');

  useEffect(() => {
    if (canFilterFirm) {
      fetchFirms();
    }
    fetchStatistics();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchStatistics();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, selectedFirm, selectedDepo]);

  // Update labels when language changes
  useEffect(() => {
    if (statistics?.labels) {
      setLabels(statistics.labels[language] || statistics.labels.tr);
    }
  }, [language, statistics]);

  const fetchFirms = async () => {
    try {
      const response = await api.get('/admin/firms');
      if (response.data.success) {
        setFirms(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch firms', error);
    }
  };

  const fetchStatistics = async () => {
    setLoading(true);
    try {
      let url = `/daily-status/statistics?start_date=${dateRange.start}&end_date=${dateRange.end}`;
      if (canFilterFirm && selectedFirm !== 'all') {
        url += `&firm_id=${selectedFirm}`;
      }
      if (selectedDepo !== 'all') {
        url += `&depo=${selectedDepo}`;
      }
      
      const response = await api.get(url);
      if (response.data.success) {
        setStatistics(response.data.data);
        setLabels(response.data.data.labels[language] || response.data.data.labels.tr);
        setColors(response.data.data.colors);
        
        // Check for orphaned records
        if (canFilterFirm) {
          const userBreakdown = response.data.data.user_breakdown || {};
          const unknownCount = Object.values(userBreakdown).filter(u => u.name === 'Unknown').length;
          if (unknownCount > 0) {
            fetchOrphanedRecords();
          }
        }
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchOrphanedRecords = async () => {
    try {
      const response = await api.get('/daily-status/orphaned-records');
      if (response.data.success) {
        setOrphanedRecords(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch orphaned records', error);
    }
  };

  const handleDeleteOrphanedRecords = async () => {
    try {
      setDeletingOrphaned(true);
      const response = await api.delete('/daily-status/orphaned-records');
      if (response.data.success) {
        toast.success(response.data.message);
        setShowOrphanedDialog(false);
        setOrphanedRecords(null);
        fetchStatistics(); // Refresh stats
      }
    } catch (error) {
      toast.error('Kayıtlar silinemedi');
    } finally {
      setDeletingOrphaned(false);
    }
  };

  const handleQuickRange = (range) => {
    setQuickRange(range);
    const now = new Date();
    
    switch (range) {
      case 'today':
        setDateRange({
          start: format(now, 'yyyy-MM-dd'),
          end: format(now, 'yyyy-MM-dd')
        });
        break;
      case 'yesterday':
        const yesterday = subDays(now, 1);
        setDateRange({
          start: format(yesterday, 'yyyy-MM-dd'),
          end: format(yesterday, 'yyyy-MM-dd')
        });
        break;
      case 'week':
        setDateRange({
          start: format(startOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
          end: format(endOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd')
        });
        break;
      case 'month':
        setDateRange({
          start: format(startOfMonth(now), 'yyyy-MM-dd'),
          end: format(endOfMonth(now), 'yyyy-MM-dd')
        });
        break;
      case 'quarter':
        const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
        const quarterEnd = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 + 3, 0);
        setDateRange({
          start: format(quarterStart, 'yyyy-MM-dd'),
          end: format(quarterEnd, 'yyyy-MM-dd')
        });
        break;
      case 'year':
      default:
        setDateRange({
          start: format(startOfYear(now), 'yyyy-MM-dd'),
          end: format(endOfYear(now), 'yyyy-MM-dd')
        });
        break;
    }
  };

  // Prepare data for bar chart
  const getBarChartData = () => {
    if (!statistics?.monthly_breakdown) return [];
    
    const monthNames = MONTH_NAMES[language] || MONTH_NAMES.tr;
    
    return Object.entries(statistics.monthly_breakdown)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([monthKey, stats]) => {
        const monthIndex = parseInt(monthKey.split('-')[1]) - 1;
        return {
          name: monthNames[monthIndex]?.substring(0, 3) || monthKey,
          fullName: monthNames[monthIndex] || monthKey,
          WORKING: stats.WORKING || 0,
          HALF_DAY: stats.HALF_DAY || 0,
          SICK: stats.SICK || 0,
          DOP: stats.DOP || 0,
          ON_LEAVE: stats.ON_LEAVE || 0,
        };
      });
  };

  // Prepare data for pie chart
  const getPieChartData = () => {
    if (!statistics?.status_counts) return [];
    
    const statusLabels = {
      WORKING: t('dailyStatus.working'),
      HALF_DAY: t('dailyStatus.halfDay'),
      ON_LEAVE: t('dailyStatus.onLeave'),
      SICK: t('dailyStatus.sick'),
      DOP: t('dailyStatus.dop')
    };
    
    // Exclude NONE, PATERNITY, FEESTDAG, ZATERDAG from pie chart
    const excludeStatuses = ['NONE', 'PATERNITY', 'FEESTDAG', 'ZATERDAG'];
    
    // Calculate person counts per status
    const getPersonCount = (status) => {
      if (!statistics?.user_breakdown) return 0;
      return Object.values(statistics.user_breakdown).filter(user => {
        const count = user.stats?.[status] || 0;
        return count > 0;
      }).length;
    };
    
    return Object.entries(statistics.status_counts)
      .filter(([status]) => !excludeStatuses.includes(status))
      .map(([status, count]) => ({
        name: statusLabels[status] || status,
        value: count,
        persons: getPersonCount(status),
        color: colors[status] || '#ccc'
      }));
  };

  // Filter users by search query
  const getFilteredUsers = () => {
    if (!statistics?.user_breakdown) return [];
    
    const users = Object.entries(statistics.user_breakdown).map(([userId, data]) => ({
      id: userId,
      name: data.name,
      external_firm_days: data.external_firm_days || 0,
      external_firm_details: data.external_firm_details || [],
      ...data.stats
    }));
    
    // Sort by name A-Z
    const sortedUsers = users.sort((a, b) => 
      (a.name || '').localeCompare(b.name || '', 'tr', { sensitivity: 'base' })
    );
    
    if (!searchQuery) return sortedUsers;
    
    return sortedUsers.filter(u =>
      turkishIncludes(u.name, searchQuery)
    );
  };

  // Export to Excel
  const exportToExcel = () => {
    const users = getFilteredUsers();
    
    if (users.length === 0) {
      toast.error(t('common.noData') );
      return;
    }

    // Prepare data for Excel
    const excelData = users.map((userData) => {
      const total = (userData.WORKING || 0) + (userData.HALF_DAY || 0) + 
                   (userData.SICK || 0) + (userData.DOP || 0) + 
                   (userData.ON_LEAVE || 0);
      return {
        [t('admin.employee') ]: userData.name,
        [labels.WORKING ]: userData.WORKING || 0,
        [labels.HALF_DAY ]: userData.HALF_DAY || 0,
        [labels.SICK ]: userData.SICK || 0,
        [labels.DOP ]: userData.DOP || 0,
        [labels.ON_LEAVE ]: userData.ON_LEAVE || 0,
        [t('workStats.externalFirm') || 'Diğer Firma']: userData.external_firm_days || 0,
        [t('common.total') ]: total
      };
    });

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('workStats.employeeBreakdown') );

    // Set column widths
    const colWidths = [
      { wch: 25 }, // Çalışan
      { wch: 12 }, // Çalıştı
      { wch: 12 }, // Yarım Gün
      { wch: 10 }, // Hasta
      { wch: 10 }, // DOP
      { wch: 10 }, // İzinli
      { wch: 12 }, // Diğer Firma
      { wch: 10 }, // Toplam
    ];
    ws['!cols'] = colWidths;

    // Generate filename with date range
    const filename = `calisma_istatistikleri_${dateRange.start}_${dateRange.end}.xlsx`;
    
    // Download file
    XLSX.writeFile(wb, filename);
    toast.success(t('workStats.exportSuccess') );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const statusCounts = {
    WORKING: statistics?.status_counts?.WORKING || 0,
    HALF_DAY: statistics?.status_counts?.HALF_DAY || 0,
    ON_LEAVE: statistics?.status_counts?.ON_LEAVE || 0,
    SICK: statistics?.status_counts?.SICK || 0,
    DOP: statistics?.status_counts?.DOP || 0
  };

  // Calculate unique person counts per status from user_breakdown
  const getPersonCountByStatus = (status) => {
    if (!statistics?.user_breakdown) return 0;
    return Object.values(statistics.user_breakdown).filter(user => {
      const count = user.stats?.[status] || 0;
      return count > 0;
    }).length;
  };

  const personCounts = {
    WORKING: getPersonCountByStatus('WORKING'),
    HALF_DAY: getPersonCountByStatus('HALF_DAY'),
    ON_LEAVE: getPersonCountByStatus('ON_LEAVE'),
    SICK: getPersonCountByStatus('SICK'),
    DOP: getPersonCountByStatus('DOP')
  };

  const barData = getBarChartData();
  const pieData = getPieChartData();
  const filteredUsers = getFilteredUsers();

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-work-statistics">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('workStats.employeeStatistics')}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
            {t('workStats.adminStatisticsDesc')}
          </p>
        </div>
        
        <Button variant="outline" size="sm" onClick={fetchStatistics} disabled={loading}>
          <RefreshCw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} />
          {t('common.refresh') }
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            {/* Quick Range Buttons */}
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500 dark:text-slate-400">{t('workStats.quickRange') }</Label>
              <div className="flex flex-wrap gap-1">
                <Button
                  variant={quickRange === 'today' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleQuickRange('today')}
                >
                  {t('workStats.today') || 'Bugün'}
                </Button>
                <Button
                  variant={quickRange === 'yesterday' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleQuickRange('yesterday')}
                >
                  {t('workStats.yesterday') || 'Dün'}
                </Button>
                <Button
                  variant={quickRange === 'week' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleQuickRange('week')}
                >
                  {t('workStats.thisWeek') || 'Bu Hafta'}
                </Button>
                <Button
                  variant={quickRange === 'month' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleQuickRange('month')}
                >
                  {t('workStats.thisMonth') }
                </Button>
                <Button
                  variant={quickRange === 'quarter' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleQuickRange('quarter')}
                >
                  {t('workStats.thisQuarter') }
                </Button>
                <Button
                  variant={quickRange === 'year' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleQuickRange('year')}
                >
                  {t('workStats.thisYear') }
                </Button>
              </div>
            </div>

            {/* Date Range */}
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500 dark:text-slate-400">{t('workStats.startDate') }</Label>
              <Input
                type="date"
                value={dateRange.start}
                onChange={(e) => {
                  setDateRange(prev => ({ ...prev, start: e.target.value }));
                  setQuickRange('custom');
                }}
                className="w-[130px] h-9 text-sm"
              />
            </div>
            
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500 dark:text-slate-400">{t('workStats.endDate') }</Label>
              <Input
                type="date"
                value={dateRange.end}
                onChange={(e) => {
                  setDateRange(prev => ({ ...prev, end: e.target.value }));
                  setQuickRange('custom');
                }}
                className="w-[130px] h-9 text-sm"
              />
            </div>

            {/* Firm Filter (Super Admin only) */}
            {canFilterFirm && (
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500 dark:text-slate-400">{t('admin.firm') }</Label>
                <Select value={selectedFirm} onValueChange={setSelectedFirm}>
                  <SelectTrigger className="w-[160px] h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('admin.allFirms') }</SelectItem>
                    {firms.map(firm => (
                      <SelectItem key={firm.id} value={firm.id}>{firm.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Depo Filter */}
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500 dark:text-slate-400">Depo</Label>
              <Select value={selectedDepo} onValueChange={setSelectedDepo}>
                <SelectTrigger className="w-[150px] h-9" data-testid="stats-depo-filter">
                  <SelectValue placeholder={t('common.allDepos')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.allDepos')}</SelectItem>
                  <SelectItem value="__none__">{t('common.noDepo')}</SelectItem>
                  {dbDepos.map(d => (
                    <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {['WORKING', 'HALF_DAY', 'ON_LEAVE', 'SICK', 'DOP'].map((status) => {
            const count = statusCounts[status] || 0;
            const persons = personCounts[status] || 0;
            const Icon = STATUS_ICONS[status] || HelpCircle;
            const color = colors[status] || '#ccc';
            const statusLabels = {
              WORKING: t('dailyStatus.working'),
              HALF_DAY: t('dailyStatus.halfDay'),
              ON_LEAVE: t('dailyStatus.onLeave'),
              SICK: t('dailyStatus.sick'),
              DOP: t('dailyStatus.dop')
            };
            return (
              <Card key={status} className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${color}20` }}
                    >
                      <Icon className="h-5 w-5" style={{ color }} />
                    </div>
                    <div>
                      <div className="flex items-baseline gap-1">
                        <p className="text-2xl font-bold" style={{ color }}>{count}</p>
                        {persons > 0 && (
                          <span className="text-xs text-slate-400">({persons} kişi)</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{statusLabels[status] || status}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bar Chart */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-500" />
              {t('workStats.monthlyBreakdown')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {barData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip 
                      formatter={(value, name) => {
                        const statusLabels = {
                          WORKING: t('dailyStatus.working'),
                          HALF_DAY: t('dailyStatus.halfDay'),
                          ON_LEAVE: t('dailyStatus.onLeave'),
                          SICK: t('dailyStatus.sick'),
                          DOP: t('dailyStatus.dop')
                        };
                        return [value, statusLabels[name] || name];
                      }}
                      labelFormatter={(label) => barData.find(d => d.name === label)?.fullName || label}
                    />
                    <Legend formatter={(value) => {
                      const statusLabels = {
                        WORKING: t('dailyStatus.working'),
                        HALF_DAY: t('dailyStatus.halfDay'),
                        ON_LEAVE: t('dailyStatus.onLeave'),
                        SICK: t('dailyStatus.sick'),
                        DOP: t('dailyStatus.dop')
                      };
                      return statusLabels[value] || value;
                    }} />
                    <Bar dataKey="WORKING" stackId="a" fill={colors.WORKING} />
                    <Bar dataKey="HALF_DAY" stackId="a" fill={colors.HALF_DAY} />
                    <Bar dataKey="SICK" stackId="a" fill={colors.SICK} />
                    <Bar dataKey="DOP" stackId="a" fill={colors.DOP} />
                    <Bar dataKey="ON_LEAVE" stackId="a" fill={colors.ON_LEAVE} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-400">
                  {t('common.noData')}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Pie Chart */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <PieChartIcon className="h-5 w-5 text-purple-500" />
              {t('workStats.statusDistribution')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, value, persons, percent }) => 
                        `${name}: ${value} (${persons} kişi)`
                      }
                      labelLine={false}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name, props) => {
                        const entry = props.payload;
                        return [`${value} gün (${entry.persons} kişi)`, name];
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-400">
                  {t('common.noData')}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* User Breakdown Table */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-5 w-5 text-emerald-500" />
              {t('workStats.employeeBreakdown') }
            </CardTitle>
            <div className="flex items-center gap-3">
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder={t('common.search') }
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={exportToExcel}
                className="flex items-center gap-2 bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                data-testid="export-excel-btn"
              >
                <Download className="h-4 w-4" />
                {t('workStats.exportExcel') }
              </Button>
            </div>
          </div>
          
          {/* Orphaned Records Warning */}
          {isSuperAdmin && orphanedRecords && orphanedRecords.total_orphaned_users > 0 && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800">
                    {orphanedRecords.total_orphaned_users} silinmiş kullanıcıya ait {orphanedRecords.total_orphaned_records} kayıt bulundu
                  </p>
                  <p className="text-sm text-amber-600">
                    Bu kayıtlar "Unknown" olarak görünüyor ve istatistikleri etkileyebilir.
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowOrphanedDialog(true)}
                className="border-amber-300 text-amber-700 hover:bg-amber-100"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Temizle
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('admin.employee')}</TableHead>
                  <TableHead className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: colors.WORKING }} />
                      {t('dailyStatus.working')}
                    </div>
                  </TableHead>
                  <TableHead className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: colors.HALF_DAY }} />
                      {t('dailyStatus.halfDay')}
                    </div>
                  </TableHead>
                  <TableHead className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: colors.SICK }} />
                      {t('dailyStatus.sick')}
                    </div>
                  </TableHead>
                  <TableHead className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: colors.DOP }} />
                      {t('dailyStatus.dop')}
                    </div>
                  </TableHead>
                  <TableHead className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: colors.ON_LEAVE }} />
                      {t('dailyStatus.onLeave')}
                    </div>
                  </TableHead>
                  <TableHead className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <div className="w-3 h-3 rounded bg-purple-500" />
                      {t('workStats.externalFirm') || 'Diğer Firma'}
                    </div>
                  </TableHead>
                  <TableHead className="text-center">{t('common.total')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((userData) => {
                    const total = (userData.WORKING || 0) + (userData.HALF_DAY || 0) + 
                                 (userData.SICK || 0) + (userData.DOP || 0) + 
                                 (userData.ON_LEAVE || 0);
                    const isSelected = selectedEmployee === userData.id;
                    return (
                      <TableRow 
                        key={userData.id}
                        onClick={() => setSelectedEmployee(isSelected ? null : userData.id)}
                        className={cn(
                          "cursor-pointer transition-all duration-200",
                          userData.name === 'Unknown'
                            ? "bg-amber-50 hover:bg-amber-100 border-l-4 border-l-amber-400"
                            : isSelected 
                              ? "bg-emerald-100 hover:bg-emerald-150 border-l-4 border-l-emerald-500" 
                              : "hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-800"
                        )}
                      >
                        <TableCell className={cn("font-medium", isSelected && "text-emerald-700", userData.name === 'Unknown' && "text-amber-700")}>
                          {userData.name === 'Unknown' ? (
                            <span className="flex items-center gap-2">
                              <AlertTriangle className="h-4 w-4 text-amber-500" />
                              Silinmiş Kullanıcı
                            </span>
                          ) : userData.name}
                        </TableCell>
                        <TableCell className="text-center">{userData.WORKING || 0}</TableCell>
                        <TableCell className="text-center">{userData.HALF_DAY || 0}</TableCell>
                        <TableCell className="text-center">{userData.SICK || 0}</TableCell>
                        <TableCell className="text-center">{userData.DOP || 0}</TableCell>
                        <TableCell className="text-center">{userData.ON_LEAVE || 0}</TableCell>
                        <TableCell className="text-center">
                          {userData.external_firm_days > 0 ? (
                            <span 
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium cursor-help"
                              title={userData.external_firm_details?.map(d => `${d.firm_name}: ${d.days} gün`).join(', ')}
                            >
                              {userData.external_firm_days}
                            </span>
                          ) : (
                            <span className="text-slate-300">-</span>
                          )}
                        </TableCell>
                        <TableCell className={cn("text-center font-semibold", isSelected && "text-emerald-700")}>{total}</TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-slate-400 py-8">
                      {t('common.noData') }
                    </TableCell>
                  </TableRow>
                )}
                {/* Toplam Satırı */}
                {filteredUsers.length > 0 && (
                  <TableRow className="bg-slate-100 dark:bg-slate-700 font-semibold border-t-2 border-slate-300">
                    <TableCell className="text-slate-700 dark:text-slate-200">
                      Toplam ({filteredUsers.length} kişi)
                    </TableCell>
                    <TableCell className="text-center text-emerald-600">
                      {filteredUsers.reduce((sum, u) => sum + (u.WORKING || 0), 0)}
                    </TableCell>
                    <TableCell className="text-center text-amber-600">
                      {filteredUsers.reduce((sum, u) => sum + (u.HALF_DAY || 0), 0)}
                    </TableCell>
                    <TableCell className="text-center text-red-600">
                      {filteredUsers.reduce((sum, u) => sum + (u.SICK || 0), 0)}
                    </TableCell>
                    <TableCell className="text-center text-orange-600">
                      {filteredUsers.reduce((sum, u) => sum + (u.DOP || 0), 0)}
                    </TableCell>
                    <TableCell className="text-center text-blue-600">
                      {filteredUsers.reduce((sum, u) => sum + (u.ON_LEAVE || 0), 0)}
                    </TableCell>
                    <TableCell className="text-center text-purple-600">
                      {filteredUsers.reduce((sum, u) => sum + (u.external_firm_days || 0), 0)}
                    </TableCell>
                    <TableCell className="text-center text-slate-800 dark:text-slate-100 text-lg">
                      {filteredUsers.reduce((sum, u) => {
                        const total = (u.WORKING || 0) + (u.HALF_DAY || 0) + (u.SICK || 0) + (u.DOP || 0) + (u.ON_LEAVE || 0);
                        return sum + total;
                      }, 0)}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Summary Card */}
      <Card className="border-0 shadow-sm bg-gradient-to-r from-emerald-50 to-teal-50">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 dark:text-slate-100">
                {t('workStats.periodSummary') }
              </h3>
              <p className="text-slate-600 dark:text-slate-300 text-sm">
                {format(new Date(dateRange.start), 'd MMMM yyyy', { locale: dateLocale })} - {format(new Date(dateRange.end), 'd MMMM yyyy', { locale: dateLocale })} {t('workStats.periodTotal') } <strong>{statistics?.total_records || 0}</strong> {t('workStats.recordsFound') }.
                {filteredUsers.length > 0 && (
                  <span> {filteredUsers.length} {t('workStats.employeesTracked') }.</span>
                )}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Orphaned Records Dialog */}
      <Dialog open={showOrphanedDialog} onOpenChange={setShowOrphanedDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-5 w-5" />
              Yetim Kayıtları Temizle
            </DialogTitle>
            <DialogDescription>
              Aşağıdaki kayıtlar artık sistemde bulunmayan kullanıcılara aittir.
            </DialogDescription>
          </DialogHeader>

          {orphanedRecords && (
            <div className="py-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-amber-600">Silinmiş Kullanıcı:</span>
                    <span className="font-bold text-amber-800 ml-2">{orphanedRecords.total_orphaned_users}</span>
                  </div>
                  <div>
                    <span className="text-amber-600">Toplam Kayıt:</span>
                    <span className="font-bold text-amber-800 ml-2">{orphanedRecords.total_orphaned_records}</span>
                  </div>
                </div>
              </div>

              <div className="max-h-64 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Kullanıcı ID</TableHead>
                      <TableHead className="text-center">Kayıt Sayısı</TableHead>
                      <TableHead>Tarih Aralığı</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orphanedRecords.records.map((record, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-mono text-xs">
                          {record.user_id.substring(0, 12)}...
                        </TableCell>
                        <TableCell className="text-center font-bold">
                          {record.record_count}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600 dark:text-slate-300">
                          {record.date_range}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">
                  <strong>Uyarı:</strong> Bu işlem geri alınamaz! Tüm yetim kayıtlar kalıcı olarak silinecektir.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOrphanedDialog(false)}>
              İptal
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteOrphanedRecords}
              disabled={deletingOrphaned}
            >
              {deletingOrphaned ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              {orphanedRecords?.total_orphaned_records || 0} Kayıt Sil
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminWorkStatistics;
